import { ColDef, ICellRendererParams, ValueGetterParams } from 'ag-grid-community';

import { BooleanStrings, FormatService, GridService } from 'tms-lib';
import { fulfilmentRoutesName } from '../../constants/fulfilment-routes';
import { lineExceptionReasonsText } from '../../constants/line-exception-reasons';
import { lineStageText } from '../../constants/line-stages';
import { pickStatusName } from '../../constants/pick-statuses';
import { SalesOrderLinePosition } from '../../models/sales/sales-order-line-position';

// A row carrying the requirement-2 position - the What's Happened tab's and the Sales Order Line
// Exceptions list's rows both do.
interface RowWithPosition {
  position?: SalesOrderLinePosition;
}

function position(params: { data?: RowWithPosition }): SalesOrderLinePosition | undefined {
  return params.data?.position;
}

// Every column reads from data.position through a valueGetter, so the cell, the column filter and
// the CSV export all see the same (translated) text. The fields are flat names of their own rather
// than 'position.x' paths: nothing in the shared grid relies on dot notation today.
function fromPosition(key: keyof SalesOrderLinePosition) {
  return { valueGetter: (params: ValueGetterParams) => position(params)?.[key] ?? '' };
}

// A quantity column. numberFormat's renderer formats a missing value as "0.00", so a row with no
// position (or no figure) would read as zero: the value stays null and the cell stays blank.
function quantityColumn(
  gridService: GridService,
  field: string,
  headerName: string,
  key: keyof SalesOrderLinePosition,
): ColDef {
  const column = gridService.numberFormat(field, headerName, {
    valueGetter: (params: ValueGetterParams) => position(params)?.[key] ?? null,
  });
  const render = column.cellRenderer as (params: ICellRendererParams) => string;
  return {
    ...column,
    cellRenderer: (params: ICellRendererParams) =>
      params.value === null || params.value === undefined || params.value === '' ? '' : render(params),
  };
}

// The requirement-2 columns of the What's Happened tab and the Sales Order Line Exceptions list
// (Sales Order What's Happened). Route, Journey, Stage and Pick Status follow UseFulfilmentRoutes
// (D-6): with routes off every line is local stock and they say nothing.
export function getLinePositionColumnDefs(
  gridService: GridService,
  formatService: FormatService,
  routesEnabled: boolean,
): ColDef[] {
  const statusDateText = (value: SalesOrderLinePosition | undefined): string => {
    if (!value?.statusDate) {
      return '';
    }
    const date = formatService.date(value.statusDate as unknown as Date);
    // "Reached" once the history table recorded the change; before that, the demand's last update
    // is the best date there is, and it says so (D-4).
    return value.statusDateReached ? $localize`Reached ${date}:date:` : $localize`Last updated ${date}:date:`;
  };

  return [
    ...(routesEnabled
      ? [
          gridService.text('positionStage', $localize`Stage`, {
            valueGetter: (params: ValueGetterParams) => lineStageText(position(params)?.stage),
          }),
        ]
      : []),
    gridService.text('positionReason', $localize`Reason`, {
      valueGetter: (params: ValueGetterParams) => lineExceptionReasonsText(position(params)?.reasons ?? []),
    }),
    ...(routesEnabled
      ? [
          gridService.text('positionRoute', $localize`Route`, {
            // A line split across source warehouses lists each of its routes ("LocalStock, ViaTransfer").
            valueGetter: (params: ValueGetterParams) => fulfilmentRoutesName(position(params)?.fulfilmentRoute),
          }),
          gridService.text('positionJourney', $localize`Journey`, fromPosition('journeyStatus')),
        ]
      : []),
    gridService.text('positionStatusDate', $localize`Status Date`, {
      valueGetter: (params: ValueGetterParams) => statusDateText(position(params)),
      // Sorted by the date itself, not by the words in front of it.
      comparator: (_a: string, _b: string, nodeA: { data?: RowWithPosition }, nodeB: { data?: RowWithPosition }) =>
        (Date.parse(nodeA?.data?.position?.statusDate ?? '') || 0) -
        (Date.parse(nodeB?.data?.position?.statusDate ?? '') || 0),
    }),
    quantityColumn(gridService, 'positionQuantityOrdered', $localize`Quantity Ordered`, 'quantityOrdered'),
    quantityColumn(gridService, 'positionQuantityAllocated', $localize`Quantity Allocated`, 'quantityAllocated'),
    quantityColumn(gridService, 'positionQuantityCut', $localize`Quantity Cut`, 'quantityCut'),
    quantityColumn(gridService, 'positionQuantityPicked', $localize`Quantity Picked`, 'quantityPicked'),
    quantityColumn(gridService, 'positionQuantityLoaded', $localize`Quantity Loaded`, 'quantityLoaded'),
    quantityColumn(gridService, 'positionQuantityDespatched', $localize`Quantity Despatched`, 'quantityDespatched'),
    quantityColumn(gridService, 'positionQuantityInvoiced', $localize`Quantity Invoiced`, 'quantityInvoiced'),
    gridService.text('positionPurchaseOrders', $localize`Purchase Orders`, fromPosition('purchaseOrders')),
    gridService.text('positionPickingLists', $localize`Picking Lists`, fromPosition('pickingLists')),
    // Each transfer with its pallet, as the API sends it: "400594 (2)".
    gridService.text('positionTransfers', $localize`Transfers (Pallet)`, fromPosition('transfers')),
    gridService.text('positionDespatchNotes', $localize`Despatch Notes`, fromPosition('despatchNotes')),
    gridService.text('positionInvoices', $localize`Invoices`, fromPosition('invoices')),
    gridService.text('positionCreditNotes', $localize`Credit Notes`, fromPosition('creditNotes')),
    gridService.text('positionCurrentWarehouse', $localize`Current Warehouse`, fromPosition('currentWarehouse')),
    // The API's Yes-or-blank string, shown as Yes in the user's language or blank (ui.md, AG-Grid).
    gridService.text('positionAllocationComplete', $localize`Allocation Complete`, {
      valueGetter: (params: ValueGetterParams) =>
        position(params)?.allocationComplete === BooleanStrings.yes ? BooleanStrings.translatedYes : '',
    }),
    ...(routesEnabled
      ? [
          gridService.text('positionPickStatus', $localize`Pick Status`, {
            valueGetter: (params: ValueGetterParams) => pickStatusName(position(params)?.pickStatus),
          }),
        ]
      : []),
    // Several stock items, each with its location, joined into one cell (D-1).
    gridService.text('positionStock', $localize`Stock`, fromPosition('stock')),
  ];
}
